import React from 'react';
import {
  ELECTRONIC_NOTICE_WORDING_VERSION,
  REQUIRED_CONSENT_WORDING,
  legalBlockDocHref,
  offeredElectronicNotice,
} from '../../services/legalDocumentsService';

/**
 * LegalConsentFields — the agreement checkboxes on the invite registration
 * page. Rendered only when redeem-invite's preview returned a `legal` block
 * (or reported it could not load one), so the page is unchanged otherwise.
 *
 * - Required box (unticked): REQUIRED_CONSENT_WORDING ("I agree to the Terms
 *   of Use and have read the Privacy Notice"), the same text redeem-invite
 *   records as consent_records.wording_text. Both links open in a new tab,
 *   never toggle the box, and are pinned to the exact version + HOA the server
 *   returned (/legal/terms?v=&h=&d=).
 * - Optional electronic-notice box (unticked), only when that document is
 *   offered with a consent_label and title. Its visible text is exactly
 *   "<consent_label> <title link>" — what the server records. No fallback.
 * - `notice` shows an amber banner (e.g. after the documents changed).
 * - `unavailable` shows a "Try again" pill instead of the boxes.
 */

export const LEGAL_CONSENT_NOTICE_ID = 'legal-consent-notice';
export const LEGAL_CONSENT_REQUIRED_ID = 'legal-consent-required';
export const LEGAL_CONSENT_OPTIONAL_ID = 'legal-consent-electronic-notice';

const linkClass =
  'font-bold text-secondary underline underline-offset-2 hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary/40 rounded';

const checkboxClass =
  'mt-0.5 h-5 w-5 shrink-0 cursor-pointer accent-secondary rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary/40 focus-visible:ring-offset-2';

const stop = (event) => event.stopPropagation();

const NewTabLink = ({ href, children }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className={linkClass} onClick={stop}>
    {children}
    <span className="sr-only"> (opens in a new tab)</span>
  </a>
);

export const LegalConsentFields = ({
  legal,
  agreed = false,
  onAgreedChange,
  noticeGranted = false,
  onNoticeChange,
  notice = '',
  unavailable = false,
  onRetry,
  retrying = false,
}) => {
  const electronicNotice = offeredElectronicNotice(legal);
  const showBoxes = Boolean(legal);

  return (
    <fieldset
      className="rounded-2xl bg-section-bg border border-amber-100/40 px-5 py-4 space-y-3 min-w-0"
      data-testid="legal-consent"
    >
      <legend className="sr-only">Terms of Use and Privacy Notice</legend>

      {notice && (
        <div
          id={LEGAL_CONSENT_NOTICE_ID}
          role="status"
          aria-live="polite"
          className="flex items-start gap-2 rounded-2xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm font-medium text-amber-900"
        >
          <span
            className="material-symbols-outlined text-secondary shrink-0"
            style={{ fontSize: '20px' }}
            aria-hidden="true"
          >
            info
          </span>
          <span>{notice}</span>
        </div>
      )}

      {unavailable && !showBoxes && (
        <button
          type="button"
          onClick={onRetry}
          disabled={retrying}
          className="inline-flex items-center justify-center gap-2 min-h-[44px] border border-secondary text-secondary px-6 py-2.5 rounded-full font-bold hover:bg-secondary hover:text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span className="material-symbols-outlined" style={{ fontSize: '20px' }} aria-hidden="true">
            refresh
          </span>
          {retrying ? 'Trying again…' : 'Try again'}
        </button>
      )}

      {showBoxes && (
        <label
          htmlFor={LEGAL_CONSENT_REQUIRED_ID}
          className="flex items-start gap-3 min-h-[44px] py-1 cursor-pointer"
        >
          <input
            id={LEGAL_CONSENT_REQUIRED_ID}
            type="checkbox"
            checked={agreed}
            onChange={(event) => onAgreedChange?.(event.target.checked)}
            required
            className={checkboxClass}
          />
          <span
            className="text-sm font-medium text-primary-dark leading-relaxed"
            data-wording-version={REQUIRED_CONSENT_WORDING.version}
          >
            {REQUIRED_CONSENT_WORDING.parts.map((part) =>
              typeof part === 'string' ? (
                part
              ) : (
                <NewTabLink key={part.docType} href={legalBlockDocHref(legal, part.docType)}>
                  {part.text}
                </NewTabLink>
              )
            )}
          </span>
        </label>
      )}

      {showBoxes && electronicNotice && (
        <label
          htmlFor={LEGAL_CONSENT_OPTIONAL_ID}
          className="flex items-start gap-3 min-h-[44px] py-1 cursor-pointer"
        >
          <input
            id={LEGAL_CONSENT_OPTIONAL_ID}
            type="checkbox"
            checked={noticeGranted}
            onChange={(event) => onNoticeChange?.(event.target.checked)}
            className={checkboxClass}
          />
          <span
            className="text-sm font-medium text-primary-dark leading-relaxed"
            data-wording-version={ELECTRONIC_NOTICE_WORDING_VERSION}
          >
            {electronicNotice.consent_label}{' '}
            <NewTabLink href={legalBlockDocHref(legal, 'electronic_notice')}>
              {electronicNotice.title}
            </NewTabLink>
          </span>
        </label>
      )}
    </fieldset>
  );
};

export default LegalConsentFields;
